import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

import fire from "/src/images/fire.jpeg";
import fire2 from "/src/images/fire2.jpeg";
import minister from "/src/images/minister.jpeg";
import minister2 from "/src/images/minister2.jpeg";
import minister3 from "/src/images/minister3.jpeg";
import minister4 from "/src/images/minister4.jpeg";

// Gallery images array
const galleryImages = [
  // Events and Engagements
  {
    id: 1,
    src: "/assets/Gallery/Engagement with Ludritz MUNICIPALITY.jpg",
    alt: "Engagement with Ludritz MUNICIPALITY",
    category: "municipality"
  },
  {
    id: 2,
    src: "/assets/Gallery/Community Engagement with OSULHF.jpg",
    alt: "Community Engagement with OSULHF",
    category: "community"
  },
  {
    id: 3,
    src: encodeURI("/assets/Gallery/Engagement with the His Excellence Dr. Nangolo Mbuba and Business Man    Sava Sava..jpg"),
    alt: "Engagement with His Excellence Dr. Nangolo Mbuba and Business Man Sava Sava",
    category: "leadership"
  },
  
  // Ministerial Visits and Official Launches
  {
    id: 4,
    src: encodeURI("/assets/Gallery/Hon Derek Klazen, the minister of Fisheries and Marine Resources, at the official launch of October Seafarers United Land and Housing Federation..jpg"),
    alt: "Hon Derek Klazen, Minister of Fisheries and Marine Resources, at the official launch of October Seafarers United Land and Housing Federation",
    category: "ministers"
  },
  {
    id: 5,
    src: encodeURI("/assets/Gallery/Hon. Utoni Nujom, the minister of Labour and Social welfare at the official launch of October Seafarers United Land and Housing Federation.jpg"),
    alt: "Hon. Utoni Nujom, Minister of Labour and Social Welfare at the official launch of October Seafarers United Land and Housing Federation",
    category: "ministers"
  },
  {
    id: 6,
    src: encodeURI("/assets/Gallery/Hon. Erastus Utoni, the minister Urban and Rular Development,  at official launch of October Seafarers United Land and Housing Federation.jpg"),
    alt: "Hon. Erastus Utoni, Minister of Urban and Rural Development, at official launch of October Seafarers United Land and Housing Federation",
    category: "ministers"
  },

  // Project Documentation
  {
    id: 7,
    src: "/assets/Gallery/WhatsApp Image 2025-05-16 at 09.42.21_3e63b4d6.jpg",
    alt: "Project Documentation and Progress Update",
    category: "documentation"
  },
  {
    id: 8,
    src: "/assets/Gallery/WhatsApp Image 2025-05-16 at 09.42.21_d98d61a5.jpg",
    alt: "Project Site and Development Plans",
    category: "documentation"
  },

  // Vision and Mission
  {
    id: 9,
    src: encodeURI("/assets/Gallery/Our motives are to end this and save as a group to purchase the land, develop it, and build a house for ourselves..jpg"),
    alt: "Our motives are to end this and save as a group to purchase the land, develop it, and build a house for ourselves",
    category: "vision"
  },
  {
    id: 10,
    src: fire,
    alt: "Our motives are to end this and save as a group to purchase the land, develop it, and build a house for ourselves.",
    category: "general"
  },
  {
    id: 11,
    src: fire2,
    alt: "Our motives are to end this and save as a group to purchase the land, develop it, and build a house for ourselves.",
    category: "general"
  },
  {
    id: 12,
    src: minister,
    alt: "Hon. Erastus Utoni, the minister Urban and Rular Development,  at official launch of October Seafarers United Land and Housing Federation",
    category: "ministers"
  },
  {
    id: 13,
    src: minister2,
    alt: "Hon. Utoni Nujom, the minister of Labour and Social welfare at the official launch of October Seafarers United Land and Housing Federation",
    category: "ministers"
  },
  {
    id: 14,
    src: minister3,
    alt: "Hon Derek Klazen, the minister of Fisheries and Marine Resources, at the official launch of October Seafarers United Land and Housing Federation.",
    category: "ministers"
  },
  {
    id: 15,
    src: minister4,
    alt: "Engagement with the His Excellence Dr. Nangolo Mbuba and Business Man    Sava Sava.",
    category: "ministers"
  },
];

// Available categories for filtering
const categories = [
  { id: "all", label: "All Images" },
  { id: "municipality", label: "Municipality Engagement" },
  { id: "community", label: "Community Engagement" },
  { id: "leadership", label: "Leadership Meetings" },
  { id: "ministers", label: "Ministerial Visits" },
  { id: "documentation", label: "Project Documentation" },
  { id: "vision", label: "Vision & Mission" },
  { id: "general", label: "General" }
];

export default function Gallery() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [filteredImages, setFilteredImages] = useState(galleryImages);
  const [activeFilter, setActiveFilter] = useState("all");
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);
  
  const handleImageLoad = (id: number) => {
    console.log(`Image loaded successfully: ${id}`);
    setLoadedImages(prev => new Set([...prev, id]));
  };

  const handleImageError = (id: number) => {
    const image = galleryImages.find(img => img.id === id);
    console.error(`Failed to load image with id: ${id}`);
    console.error(`Image path: ${image?.src}`);
    console.error(`Image alt: ${image?.alt}`);
    setFilteredImages(prev => prev.filter(img => img.id !== id));
  };
  
  // Filter gallery by category
  const filterGallery = (category: string) => {
    setActiveFilter(category);
    if (category === "all") {
      setFilteredImages(galleryImages);
    } else {
      setFilteredImages(galleryImages.filter(img => img.category === category));
    }
  };
  
  // Handle lightbox navigation
  const navigateGallery = (direction: "prev" | "next") => {
    if (selectedImage === null) return;
    
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage);
    let newIndex;
    
    if (direction === "prev") {
      newIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    } else {
      newIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    }
    
    setSelectedImage(filteredImages[newIndex].id);
  };
  
  // Handle keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImage === null) return;
      
      if (e.key === "Escape") {
        setSelectedImage(null);
      } else if (e.key === "ArrowLeft") {
        navigateGallery("prev");
      } else if (e.key === "ArrowRight") {
        navigateGallery("next");
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, filteredImages]);
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-20">
        {/* Header Section */}
        <section className="relative py-8">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-3">Our Gallery</h1>
            </div>
          </div>
        </section>
        
        {/* Gallery Filters */}
        <section className="py-8">
          <div className="container mx-auto px-4 md:px-6">
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => filterGallery(category.id)}
                  className={cn(
                    "px-6 py-2 rounded-full transition-all text-sm md:text-base",
                    activeFilter === category.id
                      ? "bg-seafarer-orange text-white shadow-lg"
                      : "bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                  )}
                >
                  {category.label}
                </button>
              ))}
            </div>
            
            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredImages.map((image, index) => (
                <div 
                  key={image.id} 
                  className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-in overflow-hidden"
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => setSelectedImage(image.id)}
                >
                  {/* Image Container - Bigger and more prominent */}
                  <div className="relative aspect-[3/2] overflow-hidden cursor-pointer">
                    <img 
                      src={image.src} 
                      alt={image.alt}
                      className={cn(
                        "w-full h-full object-cover transition-all duration-700 group-hover:scale-105",
                        !loadedImages.has(image.id) && "opacity-0"
                      )}
                      onLoad={() => handleImageLoad(image.id)}
                      onError={() => handleImageError(image.id)}
                    />
                    
                    {/* Loading Spinner */}
                    {!loadedImages.has(image.id) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900">
                        <div className="relative">
                          <div className="w-12 h-12 border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
                          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-seafarer-orange border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      </div>
                    )}
                    
                    {/* Category Badge - Always visible */}
                    <div className="absolute top-4 left-4">
                      <span className="inline-block px-3 py-1 bg-seafarer-orange/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full shadow-lg">
                        {categories.find(cat => cat.id === image.category)?.label || 'General'}
                      </span>
                    </div>
                    
                    {/* View Icon - Always visible */}
                    <div className="absolute top-4 right-4">
                      <div className="w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-seafarer-orange/80 transition-colors duration-300">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Description Section - Always visible */}
                  <div className="p-6">
                    <h3 className="text-gray-900 dark:text-white font-semibold text-base leading-tight mb-3 line-clamp-3 min-h-[3.5rem]">
                      {image.alt}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400 text-sm font-medium">
                        Click to view full image
                      </span>
                      <div className="w-8 h-8 bg-seafarer-orange rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hover Border Effect */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-seafarer-orange/50 transition-all duration-500 pointer-events-none"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Lightbox */}
        {selectedImage !== null && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              <X size={24} />
            </button>
            
            <button 
              className="absolute left-4 md:left-8 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors text-white"
              onClick={(e) => {
                e.stopPropagation();
                navigateGallery("prev");
              }}
            >
              <ChevronLeft size={24} />
            </button>
            
            <div className="max-w-5xl max-h-[80vh] relative" onClick={(e) => e.stopPropagation()}>
              {filteredImages.filter(img => img.id === selectedImage).map(image => (
                <div key={image.id} className="flex flex-col items-center">
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                  <p className="text-white text-sm md:text-base mt-4 text-center max-w-2xl">
                    {image.alt}
                  </p>
                </div>
              ))}
            </div>
            
            <button 
              className="absolute right-4 md:right-8 p-2 rounded-full bg-black/20 hover:bg-black/40 transition-colors text-white"
              onClick={(e) => {
                e.stopPropagation();
                navigateGallery("next");
              }}
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
}
